import { useState, useEffect } from "react";
import { useAnalyticsAdminApi } from "../../../hooks/useAnalyticsAdminApi";
import TinyLoader from "../../../components/loaders/TinyLoader";
import TextInput from "../../../components/common/TextInput";
import RadioButtonGroup from "../../../components/common/RadioButtonGroup";
import AnalyticsWidgetDisplayType from "../../../types/analytics/AnalyticsWidgetDisplayType";
import { CreateAnalyticsSimpleWidgetFormData } from "../../../types/analytics/admin/CreateAnalyticsSimpleWidgetFormData";
import { TextArea } from "../../../components/common";
import { useAuth } from "react-oidc-context";
import { WidgetForAnalyticsPage } from "../../../types/analytics/admin/GetWidgetsForPageApiResponse";
import { useTranslation } from "react-i18next";
import { simpleAnalyticsWidgetDisplayTypes } from "../../../helpers/analytics/analyticsWidgetDisplayTypeLabels";
import SimpleWidgetDataPointsEditor from "../../../components/admin/analytics-pages/SimpleWidgetDataPointsEditor";
import { toDataPointInputs } from "../../../helpers/analytics/simpleWidgetDataPoints";
import type { SimpleWidgetAttachedDataPoint } from "../../../helpers/analytics/simpleWidgetDataPoints";

interface CreateAnalyticsSimpleWidgetProps {
  fromPageId?: number; // Optional, used to pre-fill the page ID if provided
  onCancel: () => void;
  onSubmit: (data: CreateAnalyticsSimpleWidgetFormData) => void;
  isSubmitting?: boolean;
}

const displayTypeOptions = simpleAnalyticsWidgetDisplayTypes;

const allowMultipleAnswersOptions = [
  {
    optionId: "yes",
    value: "true",
    text: "Yes",
    isSelected: false,
    allowCustomText: false,
  },
  {
    optionId: "no",
    value: "false",
    text: "No",
    isSelected: true,
    allowCustomText: false,
  },
];

const CreateAnalyticsSimpleWidget = ({
  fromPageId,
  onCancel,
  onSubmit,
  isSubmitting = false,
}: CreateAnalyticsSimpleWidgetProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const [pageId, setPageId] = useState<number | undefined>(fromPageId);
  const [sequenceValueBeforeWidgetId, setSequenceValueBeforeWidgetId] =
    useState<number | null>(null);
  const {
    getWidgetsForPage,
    widgetsForPageLoading,
    widgetsForPageError,
    widgetsForPageResponse,
    getPages,
    pagesLoading,
    pagesError,
    pagesResponse,
  } = useAnalyticsAdminApi(auth?.user?.access_token);
  // Fetch pages on initial load
  useEffect(() => {
    getPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch widget options when pageId changes or on initial load if fromPageId
  useEffect(() => {
    if (typeof pageId === "number") {
      getWidgetsForPage(pageId, (data) => {
        setSequenceValueBeforeWidgetId(null);
      });
    } else {
      setSequenceValueBeforeWidgetId(null);
    }
    // Only run when pageId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  // Form state
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [helpText, setHelpText] = useState<string>(
    t(
      "Pages.Admin.AnalyticsPages.CreateWidget.DefaultHelpText",
      "The chart gives an overview of the outcomes. Select data to see the detail and search for individuals, or use the green arrow to download.",
    ) as string,
  );
  const [displayType, setDisplayType] =
    useState<AnalyticsWidgetDisplayType>("PIE");
  const [allowMultipleAnswers, setAllowMultipleAnswers] =
    useState<boolean>(false);
  const [xAxisLabel, setXAxisLabel] = useState<string>("");
  const [yAxisLabel, setYAxisLabel] = useState<string>("");
  const [dataPoints, setDataPoints] = useState<SimpleWidgetAttachedDataPoint[]>(
    [],
  );
  const [touched, setTouched] = useState({
    title: false,
    subTitle: false,
    helpText: false,
    dataPoints: false,
  });

  const isTitleValid = title.trim().length > 0 && title.length <= 150;
  const isSubTitleValid = subTitle.trim().length > 0 && subTitle.length <= 300;
  const isHelpTextValid = helpText.trim().length > 0 && helpText.length <= 1000;
  const isDataPointsValid = dataPoints.length > 0;
  const isPageIdValid =
    typeof pageId === "number" &&
    pagesResponse.some((p) => p.pageId === pageId);
  const isFormValid =
    isTitleValid &&
    isSubTitleValid &&
    isHelpTextValid &&
    isDataPointsValid &&
    isPageIdValid;

  // Format widget options for select
  function getWidgetOptions(
    widgets: WidgetForAnalyticsPage[]
  ): { id: string | number | null; title: string }[] {
    if (Array.isArray(widgets) && widgets.length > 0) {
      const beforeOptions = widgets.map((w) => ({
        id: w.id,
        title: t(
          "Pages.Admin.AnalyticsPages.BeforeWidget",
          "Before {{title}}",
          { title: w.title },
        ),
      }));
      return [
        ...beforeOptions,
        {
          id: null,
          title: t("Pages.Admin.AnalyticsPages.AtEnd", "At end"),
        },
      ];
    } else {
      return [
        {
          id: null,
          title: t("Pages.Admin.AnalyticsPages.CreateWidget.AtStart", "At start"),
        },
      ];
    }
  }

  const widgetOptions = getWidgetOptions(widgetsForPageResponse);
  const loadingWidgetOptions = widgetsForPageLoading;
  const widgetOptionsError = widgetsForPageError;

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (isFormValid) {
          onSubmit({
            pageId: pageId as number,
            sequenceValueBeforeWidgetId,
            title,
            subTitle,
            helpText,
            displayType,
            allowMultipleAnswers,
            xAxisLabel: xAxisLabel || undefined,
            yAxisLabel: yAxisLabel || undefined,
            dataPoints: toDataPointInputs(dataPoints),
          });
        } else {
          setTouched({
            title: true,
            subTitle: true,
            helpText: true,
            dataPoints: true,
          });
        }
      }}
    >
      <div className="flex flex-col gap-2 mt-4">
        <label htmlFor="pageId" className="font-semibold text-gray-700">
          {t("Pages.Admin.AnalyticsPages.CreateWidget.PageLabel", "Page")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <select
          id="pageId"
          value={pageId}
          onChange={(e) => {
            const val = e.target.value;
            setPageId(val === "" ? undefined : Number(val));
          }}
          className="block w-full rounded-md border-gray-300 focus:border-primary focus:ring-primary"
          disabled={pagesLoading}
        >
          <option value="">
            {t(
              "Pages.Admin.AnalyticsPages.CreateWidget.SelectPage",
              "Select a page...",
            )}
          </option>
          {pagesResponse.map((page) => (
            <option key={page.pageId} value={page.pageId}>
              {page.pageTitle}
            </option>
          ))}
        </select>
        {pagesLoading && (
          <div className="flex items-center gap-2 mt-1">
            <TinyLoader />
            <span className="text-xs text-gray-500">
              {t(
                "Pages.Admin.AnalyticsPages.CreateWidget.LoadingPages",
                "Loading pages...",
              )}
            </span>
          </div>
        )}
        {pagesError && (
          <div className="text-xs text-red-500">
            {t(
              "Pages.Admin.AnalyticsPages.CreateWidget.PageLoadError",
              "Pages could not be loaded.",
            )}
          </div>
        )}
        <div className="text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.CreateWidget.PageHelp",
            "Select the analytics page to add this widget to.",
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label
          htmlFor="sequenceValueBeforeWidgetId"
          className="font-semibold text-gray-700"
        >
          {t("Pages.Admin.AnalyticsPages.Placement", "Placement")}{" "}
          <span className="text-red-500">*</span>
        </label>
        {loadingWidgetOptions ? (
          <div className="flex items-center gap-2">
            <TinyLoader />
            <span className="text-xs text-gray-500">
              {t(
                "Pages.Admin.AnalyticsPages.CreateWidget.LoadingPlacement",
                "Loading placement options...",
              )}
            </span>
          </div>
        ) : widgetOptionsError ? (
          <div className="text-xs text-red-500">
            {t(
              "Pages.Admin.AnalyticsPages.CreateWidget.PlacementLoadError",
              "Placement options could not be loaded.",
            )}
          </div>
        ) : (
          <select
            id="sequenceValueBeforeWidgetId"
            value={sequenceValueBeforeWidgetId ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              setSequenceValueBeforeWidgetId(val === "" ? null : Number(val));
            }}
            className="block w-full rounded-md border-gray-300 focus:border-primary focus:ring-primary"
            disabled={typeof pageId !== "number" || loadingWidgetOptions}
          >
            {widgetOptions.map((opt) => (
              <option key={opt.id ?? "end"} value={opt.id ?? ""}>
                {opt.title}
              </option>
            ))}
          </select>
        )}
        <div className="text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.CreateWidget.PlacementHelp",
            "Choose where to place the new widget in the page order.",
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="title" className="font-semibold text-gray-700">
          {t(
            "Pages.Admin.AnalyticsPages.CreateWidget.TitleLabel",
            "Widget title",
          )}{" "}
          <span className="text-red-500">*</span>
        </label>
        <TextInput
          inputId="title"
          value={title}
          onChange={setTitle}
          maxLength={150}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.CreateWidget.TitlePlaceholder",
            "e.g. Outcomes overview",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
          showValidationErrors={touched.title && !isTitleValid}
          validationResult={
            touched.title
              ? {
                  isValid: isTitleValid,
                  errors: isTitleValid
                    ? []
                    : [
                        {
                          errorType: "REQUIRED",
                          customMessage: t(
                            "Pages.Admin.AnalyticsPages.CreateWidget.TitleValidation",
                            "Required, maximum 150 characters",
                          ),
                        },
                      ],
                }
              : null
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="subTitle" className="font-semibold text-gray-700">
          {t(
            "Pages.Admin.AnalyticsPages.CreateWidget.SubtitleLabel",
            "Widget subtitle",
          )}{" "}
          <span className="text-red-500">*</span>
        </label>
        <TextInput
          inputId="subTitle"
          value={subTitle}
          onChange={setSubTitle}
          maxLength={300}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.CreateWidget.SubtitlePlaceholder",
            "e.g. Detailed breakdown of outcomes",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
          showValidationErrors={touched.subTitle && !isSubTitleValid}
          validationResult={
            touched.subTitle
              ? {
                  isValid: isSubTitleValid,
                  errors: isSubTitleValid
                    ? []
                    : [
                        {
                          errorType: "REQUIRED",
                          customMessage: t(
                            "Pages.Admin.AnalyticsPages.CreateWidget.SubtitleValidation",
                            "Required, maximum 300 characters",
                          ),
                        },
                      ],
                }
              : null
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="helpText" className="font-semibold text-gray-700">
          {t(
            "Pages.Admin.AnalyticsPages.CreateWidget.HelpTextLabel",
            "Help text",
          )}{" "}
          <span className="text-red-500">*</span>
        </label>
        <TextArea
          inputId="helpText"
          value={helpText}
          onChange={setHelpText}
          maxLength={1000}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.CreateWidget.HelpTextPlaceholder",
            "Widget help text",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
          showValidationErrors={touched.helpText && !isHelpTextValid}
          validationResult={
            touched.helpText
              ? {
                  isValid: isHelpTextValid,
                  errors: isHelpTextValid
                    ? []
                    : [
                        {
                          errorType: "REQUIRED",
                          customMessage: t(
                            "Pages.Admin.AnalyticsPages.CreateWidget.HelpTextValidation",
                            "Required, maximum 1,000 characters",
                          ),
                        },
                      ],
                }
              : null
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="displayType" className="font-semibold text-gray-700">
          {t("Pages.Admin.AnalyticsPages.DisplayType", "Display type")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <select
          id="displayType"
          value={displayType}
          onChange={(e) =>
            setDisplayType(e.target.value as AnalyticsWidgetDisplayType)
          }
          className="block w-full rounded-md border-gray-300 focus:border-primary focus:ring-primary"
        >
          {displayTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(
                `Pages.Admin.AnalyticsPages.DisplayTypes.${opt.key}`,
                opt.label,
              )}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-2">
        <label className="font-semibold text-gray-700">
          {t(
            "Pages.Admin.AnalyticsPages.CreateWidget.AllowMultipleLabel",
            "Allow multiple answers",
          )}{" "}
          <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-col gap-1">
          <RadioButtonGroup
            uniqueFieldName="allowMultipleAnswers"
            containerClassNames="space-y-2"
            values={allowMultipleAnswersOptions.map((opt) => ({
              ...opt,
              text:
                opt.value === "true"
                  ? t("Common.Yes", "Yes")
                  : t("Common.No", "No"),
              isSelected: String(allowMultipleAnswers) === opt.value,
            }))}
            onChange={(updated) => {
              const selected = updated.find((x) => x.isSelected);
              setAllowMultipleAnswers(
                selected ? selected.value === "true" : false
              );
            }}
            layout="HORIZONTAL"
          />
          <div className="text-xs text-gray-500">
            {t(
              "Pages.Admin.AnalyticsPages.CreateWidget.AllowMultipleHelp",
              'Choose "Yes" when the question accepts multiple answers, such as a checkbox list.',
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="xAxisLabel" className="font-semibold text-gray-700">
          {t("Pages.Admin.AnalyticsPages.XAxisLabel", "X-axis label")}
        </label>
        <TextInput
          inputId="xAxisLabel"
          value={xAxisLabel}
          onChange={setXAxisLabel}
          maxLength={100}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.CreateWidget.XAxisPlaceholder",
            "e.g. Option",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="yAxisLabel" className="font-semibold text-gray-700">
          {t("Pages.Admin.AnalyticsPages.YAxisLabel", "Y-axis label")}
        </label>
        <TextInput
          inputId="yAxisLabel"
          value={yAxisLabel}
          onChange={setYAxisLabel}
          maxLength={100}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.CreateWidget.YAxisPlaceholder",
            "e.g. Count",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
        />
      </div>
      <SimpleWidgetDataPointsEditor
        accessToken={auth?.user?.access_token}
        rows={dataPoints}
        onChange={setDataPoints}
        canEdit
        showValidation={touched.dataPoints}
      />
      <div className="flex gap-3 justify-end mt-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("Common.Cancel", "Cancel")}
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting
            ? t("Common.Saving", "Saving")
            : t("Pages.Admin.AnalyticsPages.AddWidget", "Add simple widget")}
        </button>
      </div>
    </form>
  );
};

export default CreateAnalyticsSimpleWidget;
